import React, { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { COMPANY_SETTINGS_API, PHOTOS_API, fetchWithAuth, uploadToS3 } from '../utils/api';

// Constants for upload progress key names
export const UPLOAD_KEYS = {
  COMPANY_LOGO: "companyLogo",
  CONVENTION_LOGO: "conventionLogo"
};

// Initial state for the context
const initialState = {
  isLoading: false,
  isInitializing: true,
  isSaving: false,
  isUploading: false,
  notification: null,
  hasUnsavedChanges: false,
  existingSettings: null,
  uploadProgress: {}
};

// Create the context
const CompanySettingsContext = createContext();

// Define action types
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_INITIALIZING: 'SET_INITIALIZING',
  SET_SAVING: 'SET_SAVING',
  SET_UPLOADING: 'SET_UPLOADING',
  SET_NOTIFICATION: 'SET_NOTIFICATION',
  SET_UNSAVED_CHANGES: 'SET_UNSAVED_CHANGES',
  SET_EXISTING_SETTINGS: 'SET_EXISTING_SETTINGS',
  SET_UPLOAD_PROGRESS: 'SET_UPLOAD_PROGRESS',
  RESET_NOTIFICATION: 'RESET_NOTIFICATION'
};

// Reducer function to handle state updates
function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTIONS.SET_INITIALIZING:
      return { ...state, isInitializing: action.payload };
    case ACTIONS.SET_SAVING:
      return { ...state, isSaving: action.payload };
    case ACTIONS.SET_UPLOADING:
      return { ...state, isUploading: action.payload };
    case ACTIONS.SET_NOTIFICATION:
      return { ...state, notification: action.payload };
    case ACTIONS.RESET_NOTIFICATION:
      return { ...state, notification: null };
    case ACTIONS.SET_UNSAVED_CHANGES:
      return { ...state, hasUnsavedChanges: action.payload };
    case ACTIONS.SET_EXISTING_SETTINGS:
      return { ...state, existingSettings: action.payload };
    case ACTIONS.SET_UPLOAD_PROGRESS:
      return { 
        ...state, 
        uploadProgress: { 
          ...state.uploadProgress, 
          [action.payload.key]: action.payload.value 
        } 
      };
    default:
      return state;
  }
}

// Mock auth context (in real app, this would be imported from auth provider)
const useAuth = () => {
  // This is a mock - in a real app this would come from your authentication context
  return {
    organizerId: "org_123", // This would be the actual logged-in user's organization ID
    isAuthenticated: true,
  };
};

// Provider component
export const CompanySettingsProvider = ({ children }) => {
  const [state, dispatch] = useReducer(reducer, initialState);
  const { organizerId } = useAuth();

  // Show notification with auto-clear after 5 seconds
  const showNotification = useCallback((type, message) => {
    dispatch({ type: ACTIONS.SET_NOTIFICATION, payload: { type, message } });
    
    // Auto-clear notification after 5 seconds
    setTimeout(() => {
      dispatch({ type: ACTIONS.RESET_NOTIFICATION });
    }, 5000);
  }, []);

  // Clear notification
  const clearNotification = useCallback(() => {
    dispatch({ type: ACTIONS.RESET_NOTIFICATION });
  }, []);

  // Initialize company settings
  const initialize = useCallback(async () => {
    try {
      dispatch({ type: ACTIONS.SET_INITIALIZING, payload: true });
      const data = await fetchWithAuth(COMPANY_SETTINGS_API);
      
      if (!data) {
        throw new Error("Failed to fetch settings: No response received");
      }
      
      // Make sure organizerId is set correctly in the settings
      const settingsWithOrganizerId = {
        ...data,
        organizerId: organizerId || data.organizerId,
      };
      
      dispatch({ type: ACTIONS.SET_EXISTING_SETTINGS, payload: settingsWithOrganizerId });
      dispatch({ type: ACTIONS.SET_UNSAVED_CHANGES, payload: false });
      return settingsWithOrganizerId;
    } catch (error) {
      console.error("Error fetching settings:", error);
      showNotification("error", "Failed to load settings.");
      throw error;
    } finally {
      dispatch({ type: ACTIONS.SET_INITIALIZING, payload: false });
    }
  }, [organizerId, showNotification]);

  // File upload function
  const uploadFile = useCallback(async (file, uploadKey) => {
    if (!file) return null;

    try {
      dispatch({ type: ACTIONS.SET_UPLOADING, payload: true });
      dispatch({ 
        type: ACTIONS.SET_UPLOAD_PROGRESS, 
        payload: { key: uploadKey, value: 0 } 
      });

      console.log('Requesting upload URL for', uploadKey, '...');
      
      // Create the request payload that matches your Lambda function's expectations
      const uploadRequest = {
        fileName: file.name,
        contentType: file.type,
        type: 'company',  // This matches your Lambda's logic
        entityId: organizerId || 'default',  // Make sure organizerId is available in context
        purpose: uploadKey,  // 'companyLogo' or 'conventionLogo'
        isPublic: true
      };

      console.log('Upload request payload:', uploadRequest);

      // Make the request to get presigned URL
      const response = await fetchWithAuth(`${PHOTOS_API}/presign`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(uploadRequest)
      });

      console.log('Upload URL response:', response);

      if (!response.uploadUrl) {
        throw new Error('No upload URL received from server');
      }

      // Upload file to S3 using the presigned URL
      await uploadToS3(response.uploadUrl, file, (progress) => {
        // Handle progress updates
        console.log(`Upload progress: ${progress}%`);
        dispatch({ 
          type: ACTIONS.SET_UPLOAD_PROGRESS, 
          payload: { key: uploadKey, value: progress } 
        });
      });

      console.log(`Upload complete, public URL: ${response.publicUrl}`);

      // Return the public URL
      return response.publicUrl;

    } catch (error) {
      console.error('Error uploading file:', error);
      showNotification("error", `Failed to upload file: ${error.message}`);
      throw error;
    } finally {
      dispatch({ type: ACTIONS.SET_UPLOADING, payload: false });
      
      // Reset progress when done
      setTimeout(() => {
        dispatch({ 
          type: ACTIONS.SET_UPLOAD_PROGRESS, 
          payload: { key: uploadKey, value: 0 } 
        });
      }, 1000);
    }
  }, [organizerId, showNotification]);

  // Save settings
  const saveSettings = useCallback(async (data) => {
    dispatch({ type: ACTIONS.SET_SAVING, payload: true });
    clearNotification();

    try {
      // Ensure organizerId is set
      const dataWithOrganizerId = {
        ...data,
        organizerId,
      };

      // Send data to API
      const response = await fetchWithAuth(COMPANY_SETTINGS_API, {
        method: "PUT",
        body: JSON.stringify(dataWithOrganizerId),
      });

      if (!response) {
        throw new Error("Failed to save settings: No response received");
      }

      showNotification("success", "Settings saved successfully!");
      dispatch({ type: ACTIONS.SET_EXISTING_SETTINGS, payload: dataWithOrganizerId });
      dispatch({ type: ACTIONS.SET_UNSAVED_CHANGES, payload: false });
      return true;
    } catch (error) {
      console.error("Error saving settings:", error);
      showNotification("error", `Failed to save settings: ${error.message}`);
      return false;
    } finally {
      dispatch({ type: ACTIONS.SET_SAVING, payload: false });
    }
  }, [organizerId, clearNotification, showNotification]);

  // Set unsaved changes flag
  const setHasUnsavedChanges = useCallback((value) => {
    dispatch({ type: ACTIONS.SET_UNSAVED_CHANGES, payload: value });
  }, []);

  return (
    <CompanySettingsContext.Provider
      value={{
        state,
        initialize,
        uploadFile,
        saveSettings,
        showNotification,
        clearNotification,
        setHasUnsavedChanges,
        organizerId
      }}
    >
      {children}
    </CompanySettingsContext.Provider>
  );
};

// Hook to use the context
export const useCompanySettings = () => {
  const context = useContext(CompanySettingsContext);
  if (!context) {
    throw new Error("useCompanySettings must be used within a CompanySettingsProvider");
  }
  return context;
}; 